/**
 * The "Upcoming Events" list: one card per event (title, display dates, location, hero image),
 * each linking to the event page `/about-us/news-events/<slug>`
 * (`src/pages/about-us/news-events/[eventSlug].tsx`).
 *
 * Callers: `src/pages/about-us/news-events/index.tsx` (full list, with a period filter) and
 * `src/pages/about-us/index.tsx` (`content.minimal`: the first two events and a "View All Events"
 * link). Both pass the build-time list from `src/contentful/event/getContentfulUpcomingEvents.ts`,
 * which is sorted by ascending `date` and includes events up to two days old.
 *
 * Which events show, as the code stands:
 * - An event is meant to stay listed while it is running. The code doesn't do that: `date` is
 *   the event's first day and there is no end-date field (`eventDates` is display text).
 * - The list is filtered again in the browser: an event stays while `new Date(date)` is after
 *   the visitor's clock. `date` is `YYYY-MM-DD`, which parses as UTC midnight, so an event
 *   leaves the list when its `date` begins in UTC (the afternoon or evening before, in North
 *   American time zones) and isn't listed while it runs, whatever its `eventDates` text says.
 *   `./EventDetails.tsx` applies the same test.
 * - A period tab (`content.eventsCategories`) keeps the events whose month is one of the tab's
 *   `possibleMonths`, in any year, and whose date is after today's UTC date, so a tab also
 *   drops an event on its first day.
 * - In `minimal` mode the selection is always two entries; with only one upcoming event the
 *   second is `undefined`. The desktop cards skip it, and the mobile branch passes it to
 *   `MobileCard`, which then throws reading `heroImage`.
 * - Desktop cards render only when every display field is set; mobile cards render as given.
 * - The empty-list text "There are no upcoming events." is English in every language.
 */
import React from "react";
import { Media } from "@ryerson/frontend.next";
import { useApplication } from "@ryerson/frontend.application";
import { useTheme } from "@ryerson/frontend.theme";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { Typography } from "@ryerson/frontend.typography";
import { Container, ContentSection, Flex, FlexItem } from "@ryerson/frontend.layout";
import { Icon } from "@ryerson/frontend.assets";
import { NewsEventsStaticContent } from "../types";
import { Button } from "@ryerson/frontend.button";
import { ButtonLink, Link } from "@ryerson/frontend.navigation";
 
import {
	ProcessedContentfulUpcomingEventFields,
	ProcessedContentfulUpcomingEventsByLanguage,
} from "@contentful/event/types";
import Spacer from "@components/Shared/Spacer/Spacer";

/**
 * One period tab of the events filter, from `NewsEventsStaticContent.eventsCategories`.
 * `quarter` identifies the tab (`""` is "All Events"); `possibleMonths` are two-digit months
 * (`"01"`–`"12"`) compared with the month part of each event's `date`.
 */
export type EventsCategory = {
	label: string;
	quarter: string;
	possibleMonths: string[];
};

/** Props of `MobileCard`: one event, its position, and the whole displayed list. */
interface MobileCardProps {
	contents: any;
	index: number;
	events: any;
}

const Title = styled(Typography)`
	font-size: 37.5px;
	line-height: 45px;
`;

const DatesCategory = styled.div`
	text-align: left;
	min-height: 58px;
	height: auto;
	line-height: 58px;
	display: block;
	width: 100%;
`;
const NavContainer = styled.div`
	display: block;
	position: relative;
	width: 100%;
	height: 58px;
	box-sizing: border-box;
	margin-top: 5px;
	margin-bottom: 39px;
`;

const NavItem = styled.div`
	display: inline-block;
	height: 58px;
	line-height: 57px;
	cursor: pointer;
	width: auto;
`;
const EventCard = styled.div`
	height: 320px;
	max-width: 535px;
	margin-bottom: 10px;
`;
const CardLeft = styled(FlexItem)`
	max-width: 335px;
	width: 335px;
	height: 320px;
	${(props: any) => {
		const { theme } = props;
		return css`
			background: ${theme.colors.primary.darkerGray};
		`;
	}}
`;
const Contents = styled(Container)`
	padding-left: 45px;
	padding-right: 75px;
	${(props: any) => {
		const { theme } = props;
		return css`
			background-color: ${theme.colors.primary.darkerGray};
		`;
	}}
`;
const EventSubInfo = styled.div``;
const EventTitle = styled(Typography)`
	font-size: 20px;
	line-height: 25px;
	width: 90%;
	${(props: any) => {
		const { theme } = props;
		return css`
			@media only screen and (max-width: ${theme.breakpoints.lg}) {
				padding: 28px 0;
			}
		`;
	}}
`;

const CardRight = styled(FlexItem)`
	max-width: 200px;
	height: 320px;
`;
const CardImage = styled.img`
	width: 100%;
	height: 100%;
	object-fit: cover;
`;

const MobileFilter = styled.div`
	padding-bottom: 29px;
`;
const MobileCardSection = styled.div`
	width: 100%;
	display: block;
	padding-bottom: 45px;
	position: relative;
	margin-top: 60px;
	${(props: any) => {
		const { theme } = props;
		return css`
			background-color: ${theme.colors.primary.darkerGray};
		`;
	}}
`;

const MobileCardImage = styled.div`
	width: calc(100% - 20px);
	position: relative;
	top: -40px;
	margin-bottom: -40px;
`;
const Divider = styled.hr`
	display: block;
	width: 100%;
	height: 1px;
	margin-top: 0;
	opacity: 0.1;
	position: absolute;
	bottom: -9px;
	left: 0px;
`;
const MobileCardText = styled.div`
	width: 100%;
	padding-left: 20px;
	padding-right: 20px;
	box-sizing: border-box;
`;

const RevealBox = styled.div`
	width: calc(100% - 40px);
	position: absolute;
	top: 60px;
	left: 20px;
	height: 0px;
	overflow: hidden;
	padding: 0px 20px;
	box-sizing: border-box;
	box-shadow: 0px 10px 80px rgb(0 0 0 / 7%), 0px 10px 17.869px rgb(0 0 0 / 4%),
		0px 5px 5.32008px rgb(0 0 0 / 3%);
	background-color: #ffffff;
	z-index: 1;
`;

const MobileCardContainer = styled.div`
	min-height: 200px;
`;

const MobileDivider = styled.hr`
	display: block;
	width: 100%;
	opacity: 0.1;
`;

const CategorySelector = styled.span`
	display: inline-block;
`;

const MoreEventsButton = styled.div`
	padding: 43px 64px 0 64px;
`;

 

type UpcomingEventsListProps = {
	content: NewsEventsStaticContent;
	allUpcomingEventsByLanguage: ProcessedContentfulUpcomingEventsByLanguage;
};

/**
 * Renders the current language's upcoming events: a desktop list from `lg` up and a mobile list
 * below it, each with its own filter control. Default export; see the file header for which
 * events show.
 */
const UpcomingEventsList: React.FC<UpcomingEventsListProps> = ({
	content,
	allUpcomingEventsByLanguage,
}) => {
	const { theme } = useTheme();
	const {
		localization: { language },
	} = useApplication();
	const allEvents = allUpcomingEventsByLanguage[language];

	// `date` is "YYYY-MM-DD", read as UTC midnight: an event drops out once its date begins in UTC.
	// Events are meant to stay listed while they run; `date` is the first day, and there is no
	// end-date field to test instead.
	let upcomingEvents: ProcessedContentfulUpcomingEventFields[] = allEvents.filter(
		(event) => new Date(event.date) > new Date()
	);

	/** The two-digit month of a "YYYY-MM-DD" date. */
	function grabMonthFromDate(date: string) {
		const dates = date.split("-");
		return dates[1];
	}

	const [selectedEvents, setSelectedEvents] =
		React.useState<ProcessedContentfulUpcomingEventFields[]>();
	const [quarter, setQuater] = React.useState<string>("");
	const [filter, setFilter] = React.useState<string[]>([""]);
	const [mobileShown, setMobileShown] = React.useState<number>(6);
	const [showMobileNav, setShowMobileNav] = React.useState<boolean>(false);

	// Recomputes the selection when a period tab is picked or the language changes. Until the
	// first run, `selectedEvents` is undefined and no cards render.
	React.useEffect(() => {
		if (quarter === "") {
			if (content.minimal) {
				// Always two entries: `undefined` when fewer than two events are upcoming.
				let minimalEvents = [upcomingEvents[0], upcomingEvents[1]];
				setSelectedEvents(minimalEvents);
			} else {
				setSelectedEvents(upcomingEvents);
			}
		} else {
			// Month match only, so a tab also shows events in the same months of a later year.
			let newCurrent: ProcessedContentfulUpcomingEventFields[] = [];
			allEvents.forEach((item) => {
				if (filter.includes(grabMonthFromDate(item.date))) {
					const now = new Date();
					const today = now.toISOString().split("T")[0];
					if (item.date > today) {
						newCurrent.push(item);
					}
				}
			});
			setSelectedEvents(newCurrent);
		}
		if (content.minimal) {
			setMobileShown(2);
		} else {
			setMobileShown(6);
		}
	}, [filter, language]);

	return (
		<>
			<Media greaterThanOrEqual="lg">
				<Title
					variant="h4"
					weight="bold"
					css={css`
						margin-bottom: ${content.minimal ? "53px" : "0px"};
					`}
				>
					{content.upcomingEventsLabel}
				</Title>

				{upcomingEvents.length === 0 ? (
					<>
						<Typography variant="p">There are no upcoming events.</Typography>
					</>
				) : (
					<>
						{content.minimal ? (
							<></>
						) : (
							<>
								<DatesCategory>
									<NavContainer>
										{content.eventsCategories.map(
											(category: EventsCategory, index: number) => {
												return (
													<NavItem
														key={index}
														css={css`
															margin-right: ${index ===
															content.eventsCategories.length - 1
																? "0px"
																: "40px"};
															border-bottom: ${quarter ===
															category.quarter
																? "1px solid " +
																  theme.colors.primary.link
																: "1px solid transparent"};
														`}
														onClick={() => {
															setQuater(category.quarter);
															setFilter(category.possibleMonths);
														}}
													>
														<Typography
															variant="span"
															type="secondary"
															color={theme.colors.secondary.header}
															size="md"
															weight="bold"
															css={css`
																display: block;
																height: 57px;
																line-height: 57px;
															`}
														>
															{category.label}
														</Typography>
													</NavItem>
												);
											}
										)}
										<Divider />
									</NavContainer>
								</DatesCategory>
							</>
						)}

						<div
							css={css`
								max-height: ${content.minimal ? "654px" : "1974px"};
								overflow-x: ${content.minimal ? "hidden" : "auto"};
								overflow-y: ${content.minimal ? "hidden" : "auto"};
							`}
						>
							{/* A card renders only when every field it shows is set. This also
							skips the `undefined` second entry in `minimal` mode. The list comes
							from `src/contentful/event/helpers/index.ts` →
							`processContentfulUpcomingEvents`, which copies the fields as
							Contentful returns them. */}
							{selectedEvents &&
								selectedEvents.map(
									(event, index: number) =>
										event &&
										event.eventDates &&
										event.date &&
										event.eventLocation &&
										event.heroImage &&
										event.heroImage.file &&
										event.slug &&
										event.title && (
											<React.Fragment key={event?.title ?? index}>
												<Link to={"/about-us/news-events/" + event?.slug}>
													<EventCard key={index}>
														<Flex>
															<CardLeft theme={theme}>
																<Contents
																	vPadding="35px"
																	hPadding="45px"
																	theme={theme}
																>
																	<Flex
																		direction="column"
																		justifyContent="space-between"
																		css={css`
																			min-height: 250px;
																		`}
																	>
																		<FlexItem>
																			<EventTitle
																				theme={theme}
																				variant="h5"
																				color={
																					theme.colors
																						.primary
																						.white
																				}
																			>
																				{event?.title}
																			</EventTitle>
																		</FlexItem>
																		<FlexItem>
																			<EventSubInfo>
																				<div>
																					<Typography
																						variant="p"
																						color={
																							theme
																								.colors
																								.primary
																								.lightGray
																						}
																					>
																						{
																							event.eventDates
																						}
																					</Typography>
																				</div>
																				<div>
																					<Typography
																						variant="p"
																						color={
																							theme
																								.colors
																								.primary
																								.lightGray
																						}
																					>
																						{
																							event.eventLocation
																						}
																					</Typography>
																				</div>
																			</EventSubInfo>
																		</FlexItem>
																	</Flex>
																</Contents>
															</CardLeft>
															<CardRight>
																<CardImage
																	src={
																		event.heroImage?.file
																			? event.heroImage?.file
																					?.url +
																			  "?fm=jpg&fit=fill&w=200&h=320"
																			: ""
																	}
																	alt={event.title}
																/>
															</CardRight>
														</Flex>
													</EventCard>
												</Link>
											</React.Fragment>
										)
								)}
						</div>
						{content.minimal ? (
							<>
								<Spacer
									css={css`
										height: 43px;
									`}
								/>
								<ButtonLink
									label={content.viewAllEventsLabel}
									to={content.viewAllEventsURL}
									linkType="internal"
									type="secondary"
									size="sm"
								/>
							</>
						) : (
							<></>
						)}
					</>
				)}
			</Media>

			<Media lessThan="lg">
				<ContentSection vPadding="29px" hPadding="0">
					{upcomingEvents.length === 0 ? (
						<>
							<Typography variant="p">There are no upcoming events.</Typography>
						</>
					) : (
						<>
							{content.minimal ? (
								<></>
							) : (
								<MobileFilter>
									<label htmlFor="period">
										<Typography color={theme.colors.primary.gray}>
											{content.filterLabel}
										</Typography>
										<Typography
											variant="span"
											size="lg"
											color={theme.colors.primary.link}
										>
											{content.eventsCategories.map(
												(category: EventsCategory, index: number) => {
													return (
														<React.Fragment key={index}>
															{quarter === category.quarter ? (
																<CategorySelector
																	onClick={() => {
																		setShowMobileNav(
																			showMobileNav
																				? false
																				: true
																		);
																	}}
																>
																	{category.label}
																	<Icon
																		icon="arrow-down"
																		css={css`
																			cursor: pointer;
																			margin-left: 8px;
																		`}
																		size="xs"
																		color={
																			theme.colors.primary
																				.link
																		}
																	/>
																</CategorySelector>
															) : (
																<></>
															)}
														</React.Fragment>
													);
												}
											)}
										</Typography>
										<RevealBox
											css={css`
												background-color: ${theme.colors.primary
													.background};
												height: ${showMobileNav === true ? "230px" : "0px"};
												transition: height 0.25s ease;
											`}
										>
											{content.eventsCategories.map(
												(category: EventsCategory, index: number) => {
													return (
														<React.Fragment key={index}>
															<Typography
																variant="div"
																color={
																	quarter === category.quarter
																		? theme.colors.primary.gray
																		: theme.colors.primary
																				.header
																}
																size="md"
																weight="bold"
																css={css`
																	display: block;
																	height: 20px;
																	line-height: 20px;
																	margin-top: ${index < 1
																		? "13px"
																		: "0px"};
																	margin-bottom: 10px;
																`}
															>
																<CategorySelector
																	onClick={() => {
																		setQuater(category.quarter);
																		setFilter(
																			category.possibleMonths
																		);
																		setShowMobileNav(
																			showMobileNav
																				? false
																				: true
																		);
																	}}
																>
																	{category.label}
																</CategorySelector>
															</Typography>
															{index ===
															content.eventsCategories.length - 1 ? (
																<></>
															) : (
																<MobileDivider
																	css={css`
																		color: ${theme.colors
																			.primary.darkerGray};
																		margin-bottom: 10px;
																	`}
																/>
															)}
														</React.Fragment>
													);
												}
											)}
										</RevealBox>
									</label>
								</MobileFilter>
							)}
							<MobileCardContainer>
								<ContentSection vPadding="0" hPadding="0">
									{/* No field check here, unlike the desktop list: an `undefined`
									entry reaches `MobileCard`. */}
									{selectedEvents &&
										selectedEvents.map((event, index: number) => {
											return index < mobileShown ? (
												<MobileCard
													key={index}
													contents={event}
													index={index}
													events={selectedEvents}
												/>
											) : (
												<></>
											);
										})}
								</ContentSection>
								{selectedEvents &&
								selectedEvents.length > mobileShown &&
								!content.minimal ? (
									<MoreEventsButton>
										<Button
											label={content.loadMoreButton}
											shape="rounded"
											variant="outline"
											fullwidth={true}
											background={theme.colors.primary.secondaryBrand}
											onClick={() => {
												setMobileShown(mobileShown + 6);
											}}
										></Button>
									</MoreEventsButton>
								) : content.minimal ? (
									<>
										<ButtonLink
											label={content.viewAllEventsLabel}
											to={content.viewAllEventsURL}
											linkType="internal"
											type="primary"
										/>
									</>
								) : (
									<></>
								)}
							</MobileCardContainer>
						</>
					)}
				</ContentSection>
			</Media>
		</>
	);
};

export default UpcomingEventsList;

/**
 * One event card below `lg`, linking to the event page. Odd-indexed cards offset their image
 * 20px. Expects `contents` to be an event: it reads `contents.heroImage` without a guard. The
 * image `alt` reads `events.title`, a property the array doesn't have, so it is `undefined`.
 */
const MobileCard: React.FC<MobileCardProps> = ({ contents, index, events }) => {
	const { theme } = useTheme();
	return (
		<Link to={"/about-us/news-events/" + contents?.slug}>
			<MobileCardSection
				theme={theme}
				css={css`
					margin-bottom: ${contents && index === events.length - 1 ? "40px" : "0px"};
				`}
			>
				<MobileCardImage>
					<img
						css={css`
							width: 100%;
							height: auto;
							display: block;
							margin-left: ${index % 2 === 0 ? "0px" : "20px"};
						`}
						src={
							contents.heroImage?.file
								? contents.heroImage?.file?.url + "?fm=jpg&fit=fill&w=660&h=440"
								: ""
						}
						alt={events.title}
					/>
				</MobileCardImage>
				<MobileCardText>
					<EventTitle theme={theme} variant="h5" color={theme.colors.primary.white}>
						{contents?.title}
					</EventTitle>
					<Divider css={{ color: theme.colors.primary.white }} />

					<Typography variant="p" color={theme.colors.primary.lightGray}>
						{contents?.eventDates}
					</Typography>
					<Typography variant="p" color={theme.colors.primary.lightGray}>
						{contents?.eventLocation}
					</Typography>
				</MobileCardText>
			</MobileCardSection>
		</Link>
	);
};
